import type { AllEvents, Callback } from '../core/types';
import { events } from '../core';
import { getEventContext } from '../core/enricher';
import { getStoredConsent } from '../core/consent';

type UserDataKey =
    | 'email_address'
    | 'external_id'
    | 'user_id'
    | 'user_name'
    | 'country'
    | 'phone_number'
    | 'state'
    | 'first_name'
    | 'last_name'
    | 'city'
    | 'postal_code';

// Legacy GTM `user_data` shape (email_address/user_id/user_name), kept for
// existing tags that read it. Populated only by the `identify` handler below
// and cleared on `logout` — WE-115 fix: this used to never clear, leaking the
// previous user's PII into the next user's events within the same tab.
let user_data: Partial<Record<UserDataKey, string>> = {};

function sendGtmEvent(
    eventName: string,
    payload: Record<string, unknown> = {}
) {
    // WE-115: every destination event carries the same identity/session
    // context (event_id for dedup, anonymous_id/user_id, session_id, service)
    // — resolved centrally in the enricher, not read from a local variable.
    // Spread once, at the top level of the push (matching where the
    // pre-WE-115 event_id alone used to live) — NOT also into eventModel, or
    // every context field ends up duplicated and GTM variables/tags can
    // bind to either copy and silently drift.
    const context = getEventContext();
    const eventModel: Record<string, unknown> = { ...payload };

    // WE-115 GDPR: Consent Mode gates whether Google's own tags READ this
    // data, but the raw dataLayer push happens regardless — so without this
    // check, PII (email, user_id) still lands in `window.dataLayer` even
    // when the user declined tracking, readable by any non-Google tag.
    const piiAllowed = getStoredConsent() === 'granted';
    if (piiAllowed && Object.keys(user_data).length > 0) {
        eventModel.user_data = user_data;
    }

    // Field order matters: `...payload` is spread BEFORE the context and the
    // gated `user_id`, so a payload that happens to carry identity fields
    // (identify does) can never override the consent mask at the top level.
    window.dataLayer?.push({
        event: eventName,
        event_name: eventName,
        ...payload,
        ...context,
        user_id: piiAllowed ? context.user_id : null,
        eventModel,
    });
}

function sendCustomEvent(name: string, data: Record<string, unknown> = {}) {
    sendGtmEvent(name, data);
}

let lastIdentifiedUserId = '';

// WE-115: every event here just forwards its payload verbatim under the same
// name — listed once so a new event can't compile-check as a valid name here
// (`satisfies readonly (keyof AllEvents)[]`) yet still be forgotten as an
// actual `.on(...)` handler, the failure mode this used to have with ~30
// hand-written identical handler blocks. Events that reshape their payload
// (add_to_cart/begin_checkout/purchase) or carry side effects beyond forwarding
// (identify/logout) keep their own explicit handler below instead.
const FORWARDED_EVENTS = [
    'login_failed',
    'login',
    'sign_up',
    'trial_started',
    'session_start',
    'session_end',
    'page_view',
    'service_switched',
    'research_started',
    'research_completed',
    'charge_tokens',
    'charge_tokens_failed',
    'products_viewed',
    'report_opened',
    'report_purchased',
    'report_viewed',
    'details_viewed',
    'detailed_table_view_clicked',
    'niche_switched',
    'chat_message_sent',
    'insight_requested',
    'insight_loaded',
    'product_save_clicked',
    'product_saved',
    'launchpad_add_for_sourcing_clicked',
    'history_item_opened',
    'history_niche_favorited',
    'onboarding_step_completed',
    'onboarding_completed',
    'paywall_shown',
    'paywall_tap_purchase',
    'paywall_tap_close',
    'purchase_failed',
] as const satisfies readonly (keyof AllEvents)[];

const FORWARDED_VOID_EVENTS = [
    'idea_launchpad_opened',
    'research_history_opened',
    'onboarding_started',
] as const satisfies readonly (keyof AllEvents)[];

// A generic forEach callback can't narrow `name` to its own literal per
// iteration (a fundamental TS limitation, not an oversight) — the cast is the
// one place that erases it back to a specific event's Callback shape, which
// `sendCustomEvent`'s own untyped signature already accepts at runtime.
function registerForwardedEvent(name: (typeof FORWARDED_EVENTS)[number] | (typeof FORWARDED_VOID_EVENTS)[number]) {
    events.on(name, ((params: Record<string, unknown> = {}) => {
        sendCustomEvent(name, params);
    }) as unknown as Callback<AllEvents, typeof name>);
}

// WE-115: SetupHooks.tsx calls initPlugins()/initGtagEvents() from a
// mount effect, which React StrictMode (dev only) invokes twice — without
// this guard, every event.on() below registers a second listener and every
// destination event fires twice in dev.
let initialized = false;

const initGtagEvents = () => {
    if (initialized) return;
    initialized = true;

    FORWARDED_EVENTS.forEach(registerForwardedEvent);
    FORWARDED_VOID_EVENTS.forEach(registerForwardedEvent);

    events
        // WE-115: replaces the old `profile_update` handler. `user_data` is
        // now cleared on `logout` (see below) — previously it never was,
        // which leaked the outgoing user's PII into the next session's
        // events on the same tab.
        .on('identify', ({ user_id, email, name }) => {
            // AuthProvider now calls this on every session restore, not just
            // fresh logins — skip re-pushing for a user we already know,
            // same guard the old profile_update handler had (`sub === userSub`).
            if (user_id === lastIdentifiedUserId) {
                return;
            }

            // Kept even without consent: sendGtmEvent gates READING it, and
            // if the user grants consent later this session, subsequent
            // events immediately carry the identity.
            user_data = {
                email_address: email ?? undefined,
                user_id,
                user_name: name || 'unknown',
            };

            // WE-115 GDPR: the identify payload IS the PII — sendGtmEvent's
            // user_id mask can't help because the raw payload lands in the
            // push (top level + eventModel). Without consent, push nothing
            // and leave the dedup guard unset so the first identify AFTER the
            // user grants consent (next login/session restore) goes through.
            if (getStoredConsent() !== 'granted') {
                return;
            }
            lastIdentifiedUserId = user_id;

            sendCustomEvent('identify', { user_id, name, email });
            // Backward-compat: the pre-WE-115 adapter pushed this fact as a
            // 'user_id' custom event. Keep emitting it under the old name
            // too until any GTM triggers still bound to it are migrated.
            sendCustomEvent('user_id', { user_id, name, email });
        })
        .on('logout', () => {
            sendCustomEvent('logout');
            // WE-115 fix: previously never cleared — see module comment above.
            user_data = {};
            lastIdentifiedUserId = '';
        })
        // Forwarded like the FORWARDED_EVENTS above, plus: when the user
        // grants consent mid-session, replay the identify that was withheld
        // while consent was undecided/denied — nothing else re-emits it until
        // the next login/reload, so identity-bound GTM triggers would
        // otherwise stay silent for the rest of the session.
        .on('consent_updated', (params) => {
            sendCustomEvent('consent_updated', params);
            const userId = user_data.user_id;
            if (params.analytics_storage === 'granted' && userId && userId !== lastIdentifiedUserId) {
                lastIdentifiedUserId = userId;
                const replay = {
                    user_id: userId,
                    name: user_data.user_name ?? null,
                    email: user_data.email_address ?? null,
                };
                sendCustomEvent('identify', replay);
                sendCustomEvent('user_id', replay);
            }
        })
        .on('add_to_cart', ({ value, item_name, item_id, item_category }) => {
            sendCustomEvent('add_to_cart', {
                value,
                items: [
                    {
                        item_id: item_id,
                        item_name: item_name,
                        item_category: item_category,
                        price: value,
                    },
                ],
            });
        })
        .on('begin_checkout', ({ value, currency, priceId, name, itemCategory }) => {
            sendCustomEvent('begin_checkout', {
                value,
                currency,
                items: [
                    {
                        item_id: priceId,
                        item_name: name,
                        item_category: itemCategory,
                        price: value,
                    },
                ],
            });
        })
        // WE-115: single canonical `purchase` event (was also duplicated as
        // `subscription_purchase` -> pushed under the name 'Subscribe') — the
        // Purchase-vs-Subscribe distinction for Meta is now a GTM tag config
        // decision (keyed on `itemCategory` in the payload), not code.
        .on('purchase',
            ({
                value,
                priceId,
                name,
                transactionId,
                currency,
                itemCategory,
            }) => {
                sendCustomEvent('purchase', {
                    currency: currency,
                    transaction_id: transactionId,
                    value,
                    items: [
                        {
                            item_id: priceId,
                            item_name: name,
                            price: value,
                            item_category: itemCategory,
                        },
                    ],
                });
            }
        );
};

export default initGtagEvents;
